import { useState } from "react";
import { Plus, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  canHaveFilials,
  isFilial,
  extractStructureState,
  type CompanyStructureState,
} from "@/types/structure";
import FilialListSection from "./FilialListSection";
import MatrizSection from "./MatrizSection";
import DebugBadge from "./DebugBadge";
import CreateFilialDrawer from "./CreateFilialDrawer";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface StructureTabProps {
  company: any;
  isLoading?: boolean;
  onCreateFilial?: () => void;
  onRefresh?: () => void;
}

/**
 * Aba Matriz e Filiais - Gestão de Matriz/Filiais
 * 
 * Regras:
 * - Aba só habilita se empresa for Matriz
 * - Lista filiais existentes
 * - Permite criar filial via Drawer
 * - Não permite edição de matrizId
 * - Mostra herança de grupo quando existir
 */
export default function StructureTab({
  company,
  isLoading = false,
  onCreateFilial,
  onRefresh,
}: StructureTabProps) {
  const [isCreatingFilial, setIsCreatingFilial] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filialToRemove, setFilialToRemove] = useState<{ id: number; name: string } | null>(null);
  const utils = trpc.useUtils();

  // Mutation para desvincular filial
  const removeFilialMutation = trpc.company.removeFilial.useMutation({
    onSuccess: () => {
      toast.success("Filial excluída com sucesso", {
        description: "A empresa foi excluída permanentemente do sistema.",
      });
      setFilialToRemove(null);
      // Invalidar a query para forçar recarregamento imediato da lista
      utils.company.getMatrizAndFiliais.invalidate({ matrizId: company?.id });
      onRefresh?.();
    },
    onError: (error) => {
      toast.error("Erro ao desvincular filial", {
        description: error.message || "Não foi possível desvincular a filial. Tente novamente.",
      });
      setFilialToRemove(null);
    },
  });

  // Query para buscar matriz e filiais
  const { data: matrizAndFiliais, isLoading: isLoadingFiliais } =
    trpc.company.getMatrizAndFiliais.useQuery(
      { matrizId: company?.id },
      { enabled: !!company?.id && canHaveFilials(company) }
    ) as any;

  // Extrair estado de estrutura com segurança
  const structureState = extractStructureState(company);
  const canCreateFilials = canHaveFilials(company);
  const isFilialCompany = isFilial(company);

  // Atualizar contagem de filiais
  const filiaisCount = (matrizAndFiliais?.filiais as any[])?.length ?? 0;

  // Empresa comum: sem estrutura
  if (!canCreateFilials && !isFilialCompany) {
    return (
      <div className="space-y-6">
        <DebugBadge
          isEditMode={true}
          companyId={company?.id}
          isMatriz={company?.isMatriz}
          matrizId={company?.matrizId}
          controladoraId={company?.controladoraId}
        />
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Somente empresas Matriz podem cadastrar filiais.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Filial: mostra apenas informação da matriz
  if (isFilialCompany && !canCreateFilials) {
    return (
      <div className="space-y-6">
        <MatrizSection company={company} />
      </div>
    );
  }

  // Matriz: mostra listagem de filiais e botão de criar
  if (canCreateFilials) {
    return (
      <div className="space-y-6">
        <DebugBadge
          isEditMode={true}
          companyId={company?.id}
          isMatriz={company?.isMatriz}
          matrizId={company?.matrizId}
          controladoraId={company?.controladoraId}
        />
        {/* Seção de Informações */}
        <Card>
          <CardHeader>
            <CardTitle>Informações da Matriz</CardTitle>
            <CardDescription>
              Gerencie as filiais desta empresa
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Tipo</p>
                <p className="font-medium">Matriz</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Filiais</p>
                <p className="font-medium">
                  {filiaisCount}
                </p>
              </div>
            </div>

            {/* Mostrar herança de grupo se existir */}
            {structureState.controladoraId && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  As filiais criadas herdarão automaticamente o vínculo ao grupo econômico.
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={() => {
                setIsDrawerOpen(true);
                onCreateFilial?.();
              }}
              disabled={isLoadingFiliais || isCreatingFilial}
              className="w-full"
            >
              {isLoadingFiliais || isCreatingFilial ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Carregando...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Adicionar Filial
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Listagem de Filiais */}
        <FilialListSection
          company={company}
          filiais={matrizAndFiliais?.filiais || []}
          isLoading={isLoadingFiliais}
          onRefresh={onRefresh}
          onDelete={(filialId) => {
            const filial = (matrizAndFiliais?.filiais as any[])?.find((f: any) => f.id === filialId);
            setFilialToRemove({
              id: filialId,
              name: filial?.nomeFantasia || filial?.razaoSocial || `Filial #${filialId}`,
            });
          }}
        />

        {/* Drawer para criar filial */}
        <CreateFilialDrawer
          isOpen={isDrawerOpen}
          company={company}
          onClose={() => setIsDrawerOpen(false)}
          onSuccess={() => {
            setIsCreatingFilial(false);
            onRefresh?.();
          }}
          onError={() => {
            setIsCreatingFilial(false);
          }}
        />

        {/* Dialog de confirmação de exclusão */}
        <AlertDialog open={!!filialToRemove} onOpenChange={(open) => { if (!open) setFilialToRemove(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir filial?</AlertDialogTitle>
              <AlertDialogDescription>
                Você está prestes a excluir permanentemente <strong>{filialToRemove?.name}</strong> do sistema.
                Todos os dados vinculados a esta empresa (campanhas, questionários, avaliações) também serão excluídos. <strong>Esta ação não pode ser desfeita.</strong>
                {" "}Caso precise cadastrar esta empresa novamente, será necessário criar um novo cadastro.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removeFilialMutation.isPending}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={removeFilialMutation.isPending}
                onClick={() => {
                  if (filialToRemove) {
                    removeFilialMutation.mutate({
                      filialId: filialToRemove.id,
                      matrizId: company.id,
                    });
                  }
                }}
              >
                {removeFilialMutation.isPending ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Excluindo...</>
                ) : (
                  "Excluir permanentemente"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

      </div>
    );
  }

  // Fallback (não deve chegar aqui)
  return (
    <Alert>
      <AlertCircle className="h-4 w-4" />
      <AlertDescription>
        Não foi possível determinar o tipo de estrutura desta empresa.
      </AlertDescription>
    </Alert>
  );
}
